import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody, CardHeader } from 'shards-react';
import VChart from './VChart';

const chartOptions = {
  responsive: true,
  legend: {
    position: 'top',
  },
  scales: {
    xAxes: [
      {
        gridLines: false,
      },
    ],
    yAxes: [
      {
        id: 'A',
        type: 'linear',
        position: 'left',
        ticks: {
          min: 0,
          callback(value) {
            return Number(value).toLocaleString('en-US');
          },
        },
      },
      {
        id: 'B',
        type: 'linear',
        position: 'right',
        ticks: {
          min: 0,
          precision: 0,
        },
        gridLines: {
          drawOnChartArea: false,
        },
      },
    ],
  },
  tooltips: {
    mode: 'index',
    intersect: false,
  },
};

const formatRange = (from, to) => {
  if (!from || !to) return '';
  const fromLabel = new Date(from).toLocaleDateString();
  const toLabel = new Date(to).toLocaleDateString();
  return `${fromLabel} – ${toLabel}`;
};

const PaymentOverview = ({ meta, payments, title }) => {
  const { datasets, labels } = useMemo(() => {
    const periods = [...new Set((payments || []).map(item => item.period))].sort();
    const currencies = [...new Set((payments || []).map(item => item.currency))].sort();
    const values = new Map((payments || []).map(item => [
      `${item.period}:${item.currency}`,
      item,
    ]));
    const countByPeriod = new Map();

    payments.forEach((item) => {
      countByPeriod.set(item.period, (countByPeriod.get(item.period) || 0) + item.count);
    });

    const amountDatasets = currencies.map((currency, index) => {
      const data = periods.map((period) => {
        const item = values.get(`${period}:${currency}`);
        return item ? item.amount : 0;
      });

      return {
        label: `${currency} Amount (${data.reduce((total, amount) => total + amount, 0).toLocaleString('en-US')})`,
        data,
        order: index + 1,
        type: 'line',
        yAxisID: 'A',
      };
    });

    const counts = periods.map(period => countByPeriod.get(period) || 0);

    return {
      labels: periods,
      datasets: [
        ...amountDatasets,
        {
          label: `Payment Count (${counts.reduce((total, count) => total + count, 0)})`,
          data: counts,
          order: amountDatasets.length + 1,
          type: 'line',
          yAxisID: 'B',
          color: '#17c671',
        },
      ],
    };
  }, [payments]);

  return (
    <Card small className="h-100">
      <CardHeader className="border-bottom">
        <h6 className="m-0">{title}</h6>
        {meta.fallbackUsed && (
          <small className="text-warning">
            {`No recent payments; showing latest available data (${formatRange(
              meta.displayedFrom,
              meta.displayedTo,
            )})`}
          </small>
        )}
      </CardHeader>
      <CardBody className="pt-0">
        {payments.length > 0
          ? (
            <VChart
              labels={labels}
              datasets={datasets}
              type="bar"
              height="115"
              chartOptions={chartOptions}
            />
          )
          : <div className="text-center text-muted p-4">No successful payments in this range</div>}
      </CardBody>
    </Card>
  );
};

PaymentOverview.propTypes = {
  meta: PropTypes.shape({
    displayedFrom: PropTypes.string,
    displayedTo: PropTypes.string,
    fallbackUsed: PropTypes.bool,
  }),
  payments: PropTypes.array,
  title: PropTypes.string,
};

PaymentOverview.defaultProps = {
  meta: {},
  payments: [],
  title: 'Payments by period and currency',
};

export default PaymentOverview;
